/*global _, addiesaas */
import {Vue} from '~/addiesaas'
import { getFirstErrorMessage } from '~/lib/axios'
import {parseNumber, alert} from '../../lib/utils'
import {formatDate, parseDateTime, time2Min} from '../../lib/dates'

import * as types from '../mutation-types/tickets'

import ProviderService from '../../services/booking'
import config from '../../lib/config'

// Product details
export const bookingType = 'Tickets'
export const productType = 'reservation'
export const rateType = 'ticket'

const reservationProducts = _.get(addiesaas.products, `types.${productType}`)
export const bookableProducts = _.mapKeys(
  _.filter(_.values(reservationProducts), ({publish_zones = []}) => {
    publish_zones = _.castArray(publish_zones || [])
    return publish_zones.includes(bookingType)
  }),
  'start_date'
)
const bookableDates = _.omitBy(_.keys(bookableProducts), v => !v)

const {
  supercharged,
  TIMEZONE: timezone,
} = config

// set this true to namespace all getters, mutations, actions etc.
const namespaced = true
export const namespace = 'Tickets'
const rootNamespace = 'RacewayBooking'
export const absoluteNamespace = [rootNamespace, namespace].join('/')

const max = Math.max
const holdDefault = {
  id: null,
  start_at: null,
  expire_at: null,
  product_id: null,
  slots: 1,
  participants: 0,
  //ignore_reservation_hold_id: null
}
export const resetHold = (hold) => {
  return _.merge(
    {},
    holdDefault,
    _.omit(hold || {}, ['id', 'slots', 'participants', 'expire_at'])
  )
}

// state
const state = {
  editItem: null,
  cartItems: {},
  settings: _.merge({}, {supercharged}, {}),
  form: {
    cartId: null,
    termsAgreed: 'no',
    quantity: 0,
    selectedDate: null,
    selectedSlots: {}, // time
    holdStartsAt: null,
    holdEndsAt: null,
    timezone,
    buffer: 1800,
    contact: {
      firstName: null,
      lastName: null,
      phone: null,
      email: null
    },
  },
  retrievedSlots: {},
  retrievedAvailability: {},
  fetchingTimeslots: false,
  wasCarted: false,
  isHydrating: false,

  bookableProducts,
  bookableDates,
}

const helpers = {
  parseRetrievedTimeslots(data, product) {
    const timeslots = _.reduce(data, (slots, item, key) => {
      const quantity = _.size(item) || 0
      const date = parseDateTime(key)
      const dateValue = date.valueOf()
      const datetime = formatDate(date, 'yyyy-MM-dd HH:mm:ss') //date.toString()
      const currentDate = formatDate(date, 'd MMMM yyyy')
      const time = formatDate(date, 'h:mm a')
      const time24 = formatDate(date, 'HH:mm')

      const product_id = item.product_id = product.id

      const slot = {
        product_id,
        datetime,
        dateValue,
        date: currentDate,
        time: time,
        timeAsNumber: time2Min(time),
        time24,

        quantity,
        participants: 0,

        hold: resetHold({
          start_at: key,
          product_id
        }),
        selected: false,
        processing: false
      }

      slots[time] = slot
      return slots

    }, {})

    return timeslots
  },
  prepareReservationCartItems(items, state, getters) {
    const rItem = items[0]
    if (_.isEmpty(rItem)) {
      return []
    }

    const {product, slot, participants, date, title, description, pricing, formattedDate, formattedTime} = rItem
    if (!participants) {
      return []
    }

    const itemOptions = {
      editable: true,
      deletable: true,
      editRoute: {name: 'supercharged.ticket'},
      useTimer: true,
      componentSuffix: '-ticket',
      groupTitle: bookingType,
      title,
      description,
      // ...pricing
    }

    const {id: product_id, type, location_id} = product

    const key = _.snakeCase(bookingType)
    const uid = _.uniqueId((new Date()).valueOf() + '_')
    const id = _.filter([date, type, key, product_id, location_id, uid]).join('_')
    // const editItem = getters.bookingEditItem || {}
    // const id = editItem.id || _.filter([date, type, key, product_id, location_id, uid]).join('_')

    const count = 1
    const slots = 1
    const reservation_hold_id = slot.hold.id
    const form = getters.input()
    const contact = helpers.getContact(form.contact)

    const options = {
      id,
      slots, // numeric
      participants,
      start_at: slot.datetime,
      rate: rateType,

      contact,
      location_id,
      reservation_hold_id,
      // form
    }
    const payload = {
      product_id,
      count,
      options
    }
    const attributes = {
      product,
      form,
      slot,
      reservation_hold_id,
      formattedDate,
      formattedTime
    }
    const item = {
      ...itemOptions,
      attributes,
      id,
      type,
      payload,
    }
    return [item]
  },
  getContact(data) {
    data = data || {}
    return {
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone,
      ...(data.email ? {email: data.email}: {})
    }
  },
}

// mutations
const mutations = {
  [types.SET_EDIT_ITEM](state, item) {
    Vue.set(state, 'editItem', item)
  },
  [types.SET_CARTED](state) {
    Vue.set(state, 'wasCarted', true)
  },
  [types.SET_INPUT](state, items) {
    _.forOwn(items, (value, key) => {
      if (key.includes('.')) {
        _.set(state.form, key, value)
      } else {
        Vue.set(state.form, key, value)
      }
    })
  },
  [types.SET_TIMESLOT](state, item) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    Vue.set(state.form.selectedSlots, item.time, item)
  },
  [types.UNSET_TIMESLOT](state, item) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    Vue.delete(state.form.selectedSlots, item.time)
  },
  [types.SET_TIMESLOT_DATA](state, {time, key, value}) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    const slot = state.form.selectedSlots[time]
    if (slot) {
      Vue.set(state.form.selectedSlots[time], key, value)
    } else {
      //
    }
  },
  [types.RETRIEVING_TIMESLOTS](state, payload) {
    Vue.set(state, 'fetchingTimeslots', payload)
  },
  [types.SET_RETRIEVED_TIMESLOTS](state, payload) {
    Vue.set(state, 'retrievedSlots', payload)
  },
  [types.SET_RETRIEVED_TIMESLOT_DATA](state, {time, key, value}) {
    const slot = state.retrievedSlots[time]
    if (slot) {
      Vue.set(state.retrievedSlots[time], key, value)
    }else {
      // console.log("missing retrieved slot for " + time)
    }
  },
  [types.SET_RETRIEVED_TIMESLOT_DATA_ITEMS](state, items) {
    _.forOwn(items, ({key, value, time}) => {
        Vue.set(state.retrievedSlots[time], key, value)
      }
    )
  },
  [types.SET_RETRIEVED_AVAILABILITY](state, payload) {
    Vue.set(state, 'retrievedAvailability', payload)
  },
  [types.CLEAN_TIMESLOTS](state) {
    Vue.set(state.form, 'selectedSlots', {})
  },
  [types.SET_LOCAL_CART_ITEM](state, item) {
    Vue.set(state.cartItems, item.date || item.cartId, item)
  },
  [types.UNSET_LOCAL_CART_ITEM](state, item) {
    Vue.delete(state.cartItems, item.date || item.cartId)
  },
  [types.CLEAR_LOCAL_CART_ITEMS](state) {
    Vue.set(state, 'cartItems', {})
  },
  [types.IS_HYDRATING_DATA](state, isHydrating) {
    state.isHydrating = isHydrating !== false
  },
}

const actions = {
  setBookingEditItem({commit}, payload) {
    commit(types.SET_EDIT_ITEM, payload)
  },
  setBookingInEditMode({commit}) {
    commit(types.SET_CARTED)
  },
  setInput({commit}, payload) {
    commit(types.SET_INPUT, payload)
  },
  async retrieveTimeslots({commit, dispatch, getters}, date) {
    commit(types.RETRIEVING_TIMESLOTS, true)
    const product = getters.selectedProduct
    const product_id = product.id
    let data = {}
    try {
      //ignore_reservation_hold_id
      const payload = {
        date,
        product_id,
        time_slot_size: 1 * product.duration
      }
      data = await ProviderService.getBookingAvailability(payload)
    } catch (e) {
      const message = getFirstErrorMessage(e, 'Unable to retrieve booking slots. Please try again!')
      alert(message, {classes: 'text-danger'})
    }

    const timeslots = helpers.parseRetrievedTimeslots(data, getters.selectedProduct)
    dispatch('setRetrievedTimeslots', timeslots)
    commit(types.RETRIEVING_TIMESLOTS, false)
    return timeslots
  },
  setRetrievedTimeslots({commit}, payload) {
    commit(types.SET_RETRIEVED_TIMESLOTS, payload)
  },
  setRetrievedTimeslotDataItems({commit}, payload) {
    commit(types.SET_RETRIEVED_TIMESLOT_DATA_ITEMS, payload)
  },
  setRetrievedTimeslotData({commit}, payload) {
    commit(types.SET_RETRIEVED_TIMESLOT_DATA, payload)
  },
  cleanRetrievedTimeslots({dispatch}) {
    dispatch('setRetrievedTimeslots', {})
  },

  selectTimeslot({commit, dispatch, getters}, {time = null, participants = null, selected = null}) {
    if (!time) {
      return
    }
    const slot = getters.getSelectedTimeslot(time)
    if (!slot) {
      commit(types.SET_TIMESLOT, getters.retrievedTimeslot(time))
    }

    if (participants) {
       dispatch('setTimeslotData', {time, key: 'participants', value: participants})
    }
    if (selected) {
       dispatch('setTimeslotData', {time, key: 'selected', value: selected})
    }
  },
  deselectTimeslot({dispatch}, {time = null}) {
    if (!time) {
      return
    }
    dispatch('setTimeslotData', {time, key: 'participants', value: 0})
    dispatch('setTimeslotData', {time, key: 'selected', value: false})
  },

  setTimeslot({commit, dispatch, getters}, payload) {
    if (!getters.isHydratingData) {
        dispatch('cleanTimeslots')
    }

    const parentTime = payload.time
    let slot = getters.getSelectedTimeslot(parentTime)
    if (!slot) {
      commit(types.SET_TIMESLOT, getters.retrievedTimeslot(parentTime))
    }

    // selected timestamp, payload.selected is here used during hydrating
    const now = !payload.selected || _.isBoolean(payload.selected) ? (new Date()).valueOf() : payload.selected

    dispatch('setTimeslotData', {time: parentTime, key: 'selected', value: now})

    // when force setting of participants is required
    const participants = payload.participants || null
    if (!_.isNull(participants)) {
      dispatch('setTimeslotData', {time: parentTime, key: 'participants', value: participants})
    }

    // force setting of hold
    const hold = payload.hold || null
    if (hold) {
      dispatch('setTimeslotData', {time: parentTime, key: 'hold', value: hold})
    }
  },
  setTimeslotData({commit}, payload) {
    commit(types.SET_TIMESLOT_DATA, payload)
  },
  cleanTimeslots({dispatch, getters}) {
    _.forOwn(getters.selectedAllTimeslots, item => {
      if (item) {
        dispatch('removeTimeslot', item)
      }
    })
  },
  removeTimeslot({commit, dispatch, getters}, payload) {
    //
    if (!payload) {
      return
    }
    const parentTime = payload.time
    // unhold
    const parent = getters.selectedAllTimeslots[parentTime]
    const newHold = resetHold(parent.hold || {})
    if (parent) {
      const hold = parent.hold || {}
      if (hold && hold.id) {
        ProviderService.unhold(hold)
      }
    }

    dispatch('setTimeslotData', {time: parentTime, key: 'hold', value: newHold})
    dispatch('setTimeslotData', {time: parentTime, key: 'selected', value: false})
    dispatch('setTimeslotData', {time: parentTime, key: 'participants', value: 0})

    commit(types.UNSET_TIMESLOT, payload)
  },
  updateTimeslotSelection({dispatch}, payload) {
    if (payload.selected) {
      dispatch('setTimeslot', payload)
    } else {
      dispatch('removeTimeslot', payload)
    }
  },
  unsetAllParticipants({dispatch, getters}) {
    _.forOwn(getters.selectedTimeslots, ({time}) => {
        dispatch('setTimeslotData', {time: time, key: 'participants', value: 0})
    })
  },
  setLocalCartItem({commit}, payload) {
    commit(types.SET_LOCAL_CART_ITEM, payload)
  },
  unsetLocalCartItem({commit}, payload) {
    commit(types.UNSET_LOCAL_CART_ITEM, payload)
  },
  clearLocalCart({commit}) {
    commit(types.CLEAR_LOCAL_CART_ITEMS)
  },
  startHydratingData({commit}, payload) {
    commit(types.IS_HYDRATING_DATA, payload)
  },
}

// getters
const getters = {
  bookableProducts: state => state.bookableProducts,
  bookableDates: state => state.bookableDates,
  selectedProduct: state => {
    return state.bookableProducts[state.form.selectedDate]
  },
  selectedProductMaxQuantity: (state, getters) => {
    return (getters.selectedProduct || {}).max_participants
  },
  totalQuantity: state => parseNumber(state.form.quantity),
  holdDefaults: () => holdDefault,
  isHydratingData: state => state.isHydrating,
  bookingEditItem: state => state.editItem,
  input(state) {
    return function (item) {
      if (!item) {
        return state.form
      }
      return _.get(state.form, item)
    }
  },
  visibleSlots(state, getters) {
    const slots = getters.retrievedTimeslots
    const participants = getters.totalQuantity || 0
    const filteredSlots = _.pickBy(slots, ({quantity = 0}) => _.toFinite(quantity) >= _.toFinite(participants))
    return filteredSlots
  },
  retrievedTimeslot(state, getters) {
    return function (time) {
      return getters.retrievedTimeslots[time]
      // return getters.retrievedTimeslots.a[time] ||  getters.retrievedTimeslots.e[time]
    }
  },
  selectedAllTimeslots: state => state.form.selectedSlots || {},
  numberOfSelectedAllTimeslots: (state, getters) => Object.keys(getters.selectedAllTimeslots).length,
  selectedTimeslots(state, getters) {
    return _.filter(getters.selectedAllTimeslots, ({participants}) => participants > 0)
  },
  getSelectedTimeslot(state, getters) {
    return function (time) {
      return getters.selectedAllTimeslots[time]
    }
  },
  totalParticipantsSlotted(state, getters) {
    return _.reduce(getters.selectedTimeslots, (total, {participants = 0}) => {
      return total + _.toFinite(participants)
    }, 0)
  },
  totalParticipantsNotSlotted(state, getters) {
    return max(0, getters.totalQuantity - getters.totalParticipantsSlotted) || 0
  },
  breakupOfReservations(state, getters) {
    const timeslots = _.omitBy(getters.selectedTimeslots, ({participants}) => !_.toFinite(participants))
    const slot = _.minBy(_.values(timeslots), 'dateValue')
    if (!_.isEmpty(slot)) {
      const date = formatDate(parseDateTime(getters.input('selectedDate')), 'yyyy-MM-dd')
      const description = formatDate(slot.datetime, 'd MMMM, yyyy hh:mm a')
      const formattedDate = formatDate(slot.datetime, 'd MMMM, yyyy')
      const formattedTime = formatDate(slot.datetime, 'hh:mm a')
      const product = getters.selectedProduct || {}
      const {rates, tax: tax_amount, price, title = bookingType} = product

      const participants = _.toFinite(slot.participants)
      const rate = _.toFinite((_.find(rates, rate => rate.tag === rateType) || {}).price || price)
      const tax = _.round(_.toFinite(tax_amount) * participants, 2)
      const subtotal = _.round(rate * participants, 2)
      const total = _.round(subtotal + tax, 2)

      return [{
        product,
        slot,
        participants,
        date,
        title,
        description,
        formattedDate,
        formattedTime,

        pricing: {
          count: participants,
          rate: _.round(rate, 2),
          tax,
          subtotal,
          total
        }
      }]
    }

    return []
  },

  proposedCartItems(state, getters) {
    return [...getters.breakupOfReservations]
  },
  proposedCart(state, getters) {
    return helpers.prepareReservationCartItems(getters.proposedCartItems, state, getters)
  },

  cartTotal(state, getters) {
    return getters.proposedCartItems.reduce((total, {amount = 0}) => total + amount, 0)
  },

  retrievedTimeslots : state => state.retrievedSlots,
  isRetrievingTimeslots: state => state.fetchingTimeslots,
}

// plugins
const plugins = []

const tickets = {namespaced, namespace, absoluteNamespace, state, mutations, actions, getters, plugins}
export default tickets
